import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { C } from '../../utils/styles';
import Icon from '../Icon';

/**
 * Enrollment Trend Chart — monthly Eligible / Enrolled / FICA savings
 *
 * Until we persist monthly snapshots, we have no historical series to plot.
 * Show a friendly empty-state explaining the chart will populate as snapshots
 * accrue (this matches the spec: 'Insufficient historical data — building snapshots from today').
 */
export default function EnrollmentTrendChart({ snapshots, kpis }) {
  const hasHistory = !!snapshots?.hasHistory && Array.isArray(snapshots?.months) && snapshots.months.length >= 2;

  return (
    <div
      data-testid="dashboard-trend"
      style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
          Enrollment trend
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: C.textMid }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: C.accent }} />
            Eligible
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: C.green }} />
            Enrolled
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, borderTop: `2px dashed ${C.purple}`, height: 0 }} />
            FICA $K
          </span>
        </div>
      </div>

      {hasHistory ? (
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <LineChart data={snapshots.months} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textMid }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: C.textMid }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: C.textMid }} unit="k" />
              <Tooltip />
              <Legend />
              <Line yAxisId="left"  type="monotone" dataKey="eligible" stroke={C.accent} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="left"  type="monotone" dataKey="enrolled" stroke={C.green}  strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line yAxisId="right" type="monotone" dataKey="ficaK"    stroke={C.purple} strokeWidth={2} dot={false} strokeDasharray="6 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div
          data-testid="dashboard-trend-empty"
          style={{
            padding: '38px 20px',
            textAlign: 'center',
            color: C.textMid,
            background: C.bg,
            border: `1px dashed ${C.border}`,
            borderRadius: 10,
          }}
        >
          <Icon name="trending-up-outline" style={{ fontSize: 30, color: C.textLight, display: 'block', margin: '0 auto 8px' }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
            Insufficient historical data
          </div>
          <div style={{ fontSize: 12 }}>
            Building snapshots from today — the chart will appear once at least two months of history accrue.
          </div>
          <div style={{ marginTop: 14, display: 'inline-flex', gap: 18, fontSize: 12, color: C.textMid }}>
            <span><strong style={{ color: C.accent }}>{(kpis?.eligible || 0).toLocaleString()}</strong> eligible today</span>
            <span><strong style={{ color: C.green }}>{(kpis?.enrolled || 0).toLocaleString()}</strong> enrolled today</span>
            <span><strong style={{ color: C.purple }}>${Math.round(((kpis?.annualFica || 0) / 1000)).toLocaleString()}k</strong> FICA potential</span>
          </div>
        </div>
      )}
    </div>
  );
}
